import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { updateTaskAssignments, startProductionTask } from '../../api/productionTask.api';
import { createTaskAssignedNotification } from '../../api/notification.api';
import { getTimeEntryHistoryByTask } from '../../api/timeEntry.api';
import {
  X,
  Users,
  User,
  Building,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Clipboard,
  FileText,
  Package,
  Clock,
  Activity,
  Boxes,
  Settings,
  Calendar,
  Factory,
  QrCode,
  Hash,
  Edit2,
  Save,
  ChevronDown,
  ChevronUp,
  Timer,
  BarChart3,
  Layers,
  Tag,
  CalendarClock,
  Database,
  ArrowLeft,
  Play,
  Plus,
  Hourglass
} from 'lucide-react';

const ProductionTaskDetailModal = ({
  isOpen,
  onClose,
  task,
  onSave,
  onAssign,
  onStartTimer,
  runningEntries = [],
  mode = 'view',
  loading: externalLoading = false,
  cachedUsers = null,
  cachedTeams = null
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [fetchingTeams, setFetchingTeams] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showStartTaskConfirm, setShowStartTaskConfirm] = useState(false);
  const [startingTask, setStartingTask] = useState(false);

  // Activity selection
  const [selectedActivity, setSelectedActivity] = useState(null); // null = All Activities

  // Previous sessions (version tracking)
  const [taskHistory, setTaskHistory] = useState([]);
  const [showPreviousSessions, setShowPreviousSessions] = useState(false);
  const [continueFromEntry, setContinueFromEntry] = useState(null);
  const [fetchingHistory, setFetchingHistory] = useState(false);
  // Tracks whether the pending-task confirmation was already handled (so we go straight to start after sessions popup)
  const [pendingTaskHandled, setPendingTaskHandled] = useState(false);

  // NEW: For the tabs modal
  const [activityHistoryMap, setActivityHistoryMap] = useState(new Map());
  const [selectedActivityTab, setSelectedActivityTab] = useState(null);

  // Collect all activities from all confirmation groups into a flat list for the dropdown
  const confirmationGroupsRaw = task?.dbConfirmationGroups || task?.confirmationGroups || [];
  const allActivities = useMemo(() => {
    const acts = [];
    confirmationGroupsRaw.forEach(group => {
      (group.dbActivities || []).forEach(act => {
        if (!acts.some(a => a.activityId === act.activityId)) {
          acts.push({
            activityId: act.activityId,
            activityUUID: act.activityUUID,
            description: act.activityDescription || act.activityId,
            operationId: act.operationId,
            plannedDuration: act.plannedDuration,
          });
        }
      });
    });
    return acts;
  }, [confirmationGroupsRaw]);

  const notificationSentRef = useRef(false);
  const saveInProgressRef = useRef(false);

  // Assignment data state - support multiple users and teams
  const [assignmentData, setAssignmentData] = useState({
    userIds: [],
    teamIds: []
  });

  // Name maps populated from backend-resolved assignment names (no API fetch needed for view mode)
  const [userNameMap, setUserNameMap] = useState({});
  const [teamNameMap, setTeamNameMap] = useState({});

  // Current mode (view/edit)
  const [currentMode, setCurrentMode] = useState(mode);

  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    schedule: false,
    barcode: false,
    activities: false,
    materialInputs: false,
    materialOutputs: false,
    productionTasks: false,
    confirmationGroups: false,
    metadata: false
  });

  // Real users and teams data from API - use cached props if available
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  // Check if task is completed (processingStatusCode '3' or status 'COMPLETED')
  const isTaskCompleted = useMemo(() => {
    // Check in dbProductionTasks
    if (task?.dbProductionTasks && task.dbProductionTasks.length > 0) {
      return task.dbProductionTasks.some(t =>
        t.processingStatusCode === '3' ||
        t.processingStatusCode === 'COMPLETED' ||
        t.status === 'COMPLETED'
      );
    }
    // Check in the task itself
    return task?.processingStatusCode === '3' ||
      task?.processingStatusCode === 'COMPLETED' ||
      task?.status === 'COMPLETED';
  }, [task]);

  // Sync from cached props when available
  useEffect(() => {
    if (cachedUsers && cachedUsers.length > 0) {
      setUsers(cachedUsers);
      setFetchingUsers(false);
    }
  }, [cachedUsers]);

  useEffect(() => {
    if (cachedTeams && cachedTeams.length > 0) {
      setTeams(cachedTeams);
      setFetchingTeams(false);
    }
  }, [cachedTeams]);

  // Helper function to get auth headers - same as AssignTaskModal
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fetch real users and teams from API ONLY in edit mode (needed for assignment dropdowns)
  // In view mode, names come from backend-resolved assignment fields (no security-sensitive fetch)
  useEffect(() => {
    const fetchData = async () => {
      if (isOpen && currentMode === 'edit' && !cachedUsers?.length && !cachedTeams?.length) {
        setFetchingUsers(true);
        setFetchingTeams(true);

        try {
          // Fetch users - lightweight lookup (same as AssignTaskModal)
          try {
            const usersResponse = await fetch('/api/auth/lookup', {
              headers: getAuthHeaders()
            });

            if (!usersResponse.ok) {
              throw new Error(`HTTP error! status: ${usersResponse.status}`);
            }

            const usersResult = await usersResponse.json();

            if (usersResult.isSuccess) {
              const usersData = usersResult.data || [];
              const formattedUsers = usersData.map(user => ({
                id: user.id,
                name: user.name
              }));
              setUsers(formattedUsers);
            }
          } catch (usersError) {
            console.error('Failed to fetch users:', usersError);
            setUsers([]);
          } finally {
            setFetchingUsers(false);
          }

          // Fetch teams - same as AssignTaskModal
          try {
            const teamsResponse = await fetch('/api/teams/lookup', {
              headers: getAuthHeaders()
            });

            if (teamsResponse.status === 401) {
              console.warn('Unauthorized to fetch teams');
              setTeams([]);
              return;
            }

            if (!teamsResponse.ok) {
              throw new Error(`HTTP error! status: ${teamsResponse.status}`);
            }

            const teamsResult = await teamsResponse.json();

            let teamsArray = [];
            if (Array.isArray(teamsResult)) {
              teamsArray = teamsResult;
            } else if (teamsResult && teamsResult.data && Array.isArray(teamsResult.data)) {
              teamsArray = teamsResult.data;
            } else if (teamsResult && teamsResult.isSuccess && teamsResult.data) {
              teamsArray = teamsResult.data;
            }

            setTeams(teamsArray || []);

          } catch (teamsError) {
            console.error('Failed to fetch teams:', teamsError);
            setTeams([]);
          } finally {
            setFetchingTeams(false);
          }

        } catch (error) {
          console.error('Error in fetchData:', error);
          setError('Failed to load data. Please try again.');
        }
      }
    };

    if (isOpen && currentMode === 'edit' && !cachedUsers?.length && !cachedTeams?.length) {
      fetchData();
    }
  }, [isOpen, currentMode, cachedUsers, cachedTeams]);

  // Extract assignments from dbProductionTasks and set in state
  useEffect(() => {
    if (task) {
      // Extract unique assignments from dbProductionTasks
      const uniqueUserIds = new Set();
      const uniqueTeamIds = new Set();
      const userNames = {};
      const teamNames = {};

      if (task.dbProductionTasks && task.dbProductionTasks.length > 0) {
        task.dbProductionTasks.forEach(productionTask => {
          if (productionTask.assignments && productionTask.assignments.length > 0) {
            productionTask.assignments.forEach(assignment => {
              if (assignment.userId) {
                uniqueUserIds.add(assignment.userId);
                // Collect backend-resolved names
                if (assignment.userName) {
                  userNames[assignment.userId] = assignment.userName;
                }
              }
              if (assignment.teamId && assignment.teamId !== "" && assignment.teamId !== "NULL") {
                uniqueTeamIds.add(assignment.teamId);
                if (assignment.teamName) {
                  teamNames[assignment.teamId] = assignment.teamName;
                }
              }
            });
          }
        });
      }

      setAssignmentData({
        userIds: Array.from(uniqueUserIds),
        teamIds: Array.from(uniqueTeamIds)
      });
      setUserNameMap(userNames);
      setTeamNameMap(teamNames);
    }
  }, [task]);

  // Add this helper function near your other helper functions (around line 200-250)
  const formatDateTime = useCallback((entry) => {
    if (!entry?.date) return 'Date unknown';

    const dateObj = new Date(entry.date);
    const formattedDate = dateObj.toLocaleDateString([], {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });

    if (entry.startTime) {
      const [hours, minutes] = entry.startTime.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      const formattedTime = `${hour12}:${minutes} ${ampm}`;
      return `${formattedDate}, ${formattedTime}`;
    }

    return formattedDate;
  }, []);

  // Memoized helper functions to get names from IDs
  // First checks backend-resolved name maps, then falls back to fetched users/teams (edit mode)
  const getUserName = useCallback((userId) => {
    if (userNameMap[userId]) return userNameMap[userId];
    const user = users.find(u => u.id === userId);
    return user ? user.name : null;
  }, [userNameMap, users]);

  const getTeamName = useCallback((teamId) => {
    if (teamNameMap[teamId]) return teamNameMap[teamId];
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : null;
  }, [teamNameMap, teams]);

  // Memoized format date helper
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return dateString;
    }
  }, []);

  // Memoized format duration helper
  const formatDuration = useCallback((duration) => {
    if (!duration) return '-';
    if (duration === 'PT0S') return '0';
    return duration
      .replace('PT', '')
      .replace('H', 'h ')
      .replace('M', 'm ')
      .replace('S', 's');
  }, []);

  const formatTotalTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  // Memoized status helpers
  const getStatusColorClass = useCallback((statusCode) => {
    switch (statusCode) {
      case '4': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
      case '3': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case '2': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200';
      case '1': return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600';
    }
  }, []);

  const getStatusIcon = useCallback((statusCode) => {
    switch (statusCode) {
      case '4': return <CheckCircle className="w-3 h-3" />;
      case '3': return <Clock className="w-3 h-3" />;
      case '2': return <Timer className="w-3 h-3" />;
      default: return <Tag className="w-3 h-3" />;
    }
  }, []);

  const getStatusDisplay = useCallback((task) => {
    if (task.lifeCycleStatusDescription) {
      return task.lifeCycleStatusDescription;
    }
    if (task.status) {
      return task.status.replace('-', ' ').charAt(0).toUpperCase() + task.status.slice(1);
    }
    return 'Unknown';
  }, []);

  // FIXED: Helper function to get the latest version from history
  const getLatestVersion = useCallback((history) => {
    if (!history || history.length === 0) return null;

    // Sort by version number descending and get the first one
    const latestByVersion = [...history].sort((a, b) => (b.version || 0) - (a.version || 0))[0];

    // Also check if there's an entry explicitly marked as latest
    const markedLatest = history.find(h => h.isLatestVersion === true);

    // Return the marked latest if it exists and has a higher version, otherwise return the highest version
    if (markedLatest && markedLatest.version >= (latestByVersion?.version || 0)) {
      return markedLatest;
    }
    return latestByVersion;
  }, []);

  // FIXED: Fetch history for all activities when modal opens
  useEffect(() => {
    const fetchAllActivitiesHistory = async () => {
      // Use productionLotId as the referenceProductionTaskId
      const referenceTaskId = task?.productionLotId;

      if (!referenceTaskId || !allActivities.length) {
        console.log('No task ID or activities found', { referenceTaskId, activitiesCount: allActivities.length });
        return;
      }

      try {
        console.log('Fetching history for reference task ID:', referenceTaskId);
        const histRes = await getTimeEntryHistoryByTask(referenceTaskId);
        const allHistory = histRes?.data || [];

        console.log('Raw history entries count:', allHistory.length);
        console.log('Available activities from task:', allActivities.map(a => ({
          id: a.activityId,
          description: a.description
        })));
        console.log('History entries activities:', [...new Set(allHistory.map(e => e.activityName))]);

        const historyByActivity = new Map();

        // Group history by activityId
        allActivities.forEach(activity => {
          // IMPROVED MATCHING: Match by activityId OR by activityName
          const activityHistory = allHistory.filter(entry => {
            // Direct match by ID
            if (entry.activityId === activity.activityId) {
              console.log(`Match found by ID: ${activity.activityId}`);
              return true;
            }

            // Match by name (case-insensitive and trimmed)
            const entryActivityName = (entry.activityName || '').trim().toUpperCase();
            const activityDescription = (activity.description || '').trim().toUpperCase();
            const matchesByName = entryActivityName === activityDescription;

            if (matchesByName) {
              console.log(`Match found by name: ${entry.activityName} === ${activity.description}`);
            }

            return matchesByName;
          });

          // Sort by version number descending
          const sortedHistory = [...activityHistory].sort((a, b) => (b.version || 0) - (a.version || 0));

          console.log(`Activity "${activity.description}" (ID: ${activity.activityId}) has ${sortedHistory.length} entries`);
          if (sortedHistory.length > 0) {
            sortedHistory.forEach(h => {
              console.log(`  - Version ${h.version}: ${h.hours}h, AccSec: ${h.accumulatedSeconds}s, By: ${h.employeeName}`);
            });
          }

          historyByActivity.set(activity.activityId, sortedHistory);
        });

        setActivityHistoryMap(historyByActivity);

        // Check if any history was found at all
        const totalHistoryEntries = Array.from(historyByActivity.values()).reduce((sum, arr) => sum + arr.length, 0);
        if (totalHistoryEntries === 0 && allHistory.length > 0) {
          console.warn('History entries exist but none matched the activities!');
          console.warn('Activity names from task:', allActivities.map(a => a.description));
          console.warn('Activity names from history:', [...new Set(allHistory.map(e => e.activityName))]);
        }

      } catch (err) {
        console.error('Failed to fetch activity history:', err);
        toast.error('Failed to load activity history');
      }
    };

    if (showPreviousSessions && allActivities.length > 0) {
      fetchAllActivitiesHistory();
    }
  }, [showPreviousSessions, task, allActivities]);

  // Handle save
  const handleSave = async () => {
    // Prevent multiple simultaneous save operations
    if (saveInProgressRef.current) {
      console.log('⚠️ Save already in progress, skipping...');
      return;
    }

    if (!task) return;

    setLoading(true);
    setError(null);
    saveInProgressRef.current = true;

    try {
      // Get the production task details
      let productionTaskUuid = null;
      let productionTaskNumber = null;

      if (task.dbProductionTasks && task.dbProductionTasks.length > 0) {
        const productionTask = task.dbProductionTasks[0];
        productionTaskUuid = productionTask.id;
        productionTaskNumber = productionTask.productionTaskId; // Numeric ID (23700)
      }

      if (!productionTaskUuid) {
        throw new Error('Production Task database ID not found');
      }

      // Get current user
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const currentUserId = currentUser?.id;

      // Prepare the payload for task assignment
      const payload = {
        productionTaskId: productionTaskUuid,
        userIds: assignmentData.userIds || [],
        teamIds: assignmentData.teamIds || []
      };


      // Call the updateTaskAssignments API first
      const response = await updateTaskAssignments(payload);

      // Notifications are sent automatically by the backend in AddAssignmentsAsync
      // No need to call createTaskAssignedNotification separately
      const usersToNotify = (assignmentData.userIds || []).filter(userId => userId !== currentUserId);
      if (usersToNotify.length > 0) {
        toast.success(`Task assigned and notifications sent to ${usersToNotify.length} user(s)`, {
          duration: 3000
        });
      } else {
        toast.success('Task assignments updated successfully!', { duration: 3000 });
      }

      // Show success state
      setSuccess(true);

      // Switch back to view mode after delay
      setTimeout(() => {
        setCurrentMode('view');
        setSuccess(false);

        if (onSave) {
          onSave({
            ...task,
            dbProductionTasks: task.dbProductionTasks?.map(pt => ({
              ...pt,
              assignments: [
                ...(assignmentData.userIds?.map(userId => ({
                  userId,
                  teamId: null,
                  productionTaskId: productionTaskUuid
                })) || []),
                ...(assignmentData.teamIds?.map(teamId => ({
                  teamId,
                  userId: null,
                  productionTaskId: productionTaskUuid
                })) || [])
              ]
            }))
          });
        }

        // Reset flags after completion
        saveInProgressRef.current = false;
      }, 1500);

    } catch (err) {
      let errorMessage = 'Failed to save changes. Please try again.';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      console.error('❌ Error in handleSave:', errorMessage);
      setError(errorMessage);
      toast.error(errorMessage, { duration: 4000 });

      // Reset flags on error
      saveInProgressRef.current = false;

    } finally {
      setLoading(false);
    }
  };

  // Handle assign button click
  const handleAssignClick = () => {
    if (onAssign) {
      onAssign(task);
    }
    onClose();
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setCurrentMode('view');
    // Reset assignment data to original values by re-extracting from task
    if (task) {
      const uniqueUserIds = new Set();
      const uniqueTeamIds = new Set();

      if (task.dbProductionTasks && task.dbProductionTasks.length > 0) {
        task.dbProductionTasks.forEach(productionTask => {
          if (productionTask.assignments && productionTask.assignments.length > 0) {
            productionTask.assignments.forEach(assignment => {
              if (assignment.userId) {
                uniqueUserIds.add(assignment.userId);
              }
              if (assignment.teamId) {
                uniqueTeamIds.add(assignment.teamId);
              }
            });
          }
        });
      }

      setAssignmentData({
        userIds: Array.from(uniqueUserIds),
        teamIds: Array.from(uniqueTeamIds)
      });
    }
  };

  // Handle start timer click - ALWAYS show activity selection modal
  const handleStartTimerClick = async () => {
    if (isTaskCompleted) {
      toast.error('This task is completed and cannot be started again');
      return;
    }

    // Always show activity selection modal first
    setFetchingHistory(true);
    try {
      // Prefer productionLotId — this matches ReferenceProductionTaskId stored on time entries
      const orgTaskId = task?.productionLotId || task?.dbProductionTasks?.[0]?.productionTaskId;

      // Fetch history to show in the modal
      if (orgTaskId && allActivities.length > 0) {
        const histRes = await getTimeEntryHistoryByTask(orgTaskId);
        const allHistory = histRes?.data || [];

        // Filter history by selected activity (or all if none selected)
        const filteredHistory = selectedActivity
          ? allHistory.filter(entry => entry.activityId === selectedActivity.activityId || entry.activityName === selectedActivity.description)
          : allHistory;

        setTaskHistory(filteredHistory);
      }

      // ALWAYS show the activity selection modal, regardless of history
      setShowPreviousSessions(true);

    } catch (err) {
      console.error('Failed to fetch history:', err);
      // On error, still show the modal (with empty history)
      setShowPreviousSessions(true);
    } finally {
      setFetchingHistory(false);
    }
  };

  // FIXED: Handle continue from activity selection
  const handleContinueFromActivity = async (activity, entry = null) => {
    const history = activityHistoryMap.get(activity.activityId) || [];
    const latestVersion = entry || getLatestVersion(history);

    setShowPreviousSessions(false);
    setSelectedActivity(activity);

    // Get dbTasks for pending check
    const dbTasks = task?.dbProductionTasks || [];
    const hasPendingTask = dbTasks.length === 0 || dbTasks.some(t =>
      !t.processingStatusCode || t.processingStatusCode === '1'
    );

    // Check if task is pending
    if (hasPendingTask && !pendingTaskHandled) {
      setContinueFromEntry(latestVersion);
      setShowStartTaskConfirm(true);
      return;
    }

    if (latestVersion && history.length > 0) {
      // FIXED: Calculate total accumulated seconds correctly
      // hours from the entry + accumulatedSeconds from the entry
      const totalAccumulatedSeconds = Math.round((latestVersion.hours || 0) * 3600) + (latestVersion.accumulatedSeconds || 0);

      console.log(`Continuing from version ${latestVersion.version}:`, {
        hours: latestVersion.hours,
        accumulatedSeconds: latestVersion.accumulatedSeconds,
        totalSeconds: totalAccumulatedSeconds,
        entryId: latestVersion.id
      });

      onStartTimer(task, totalAccumulatedSeconds, latestVersion.id, activity);
      toast.success(
        `Continuing ${activity.description} from version ${latestVersion.version}`
      );
    } else {
      // No previous sessions - start fresh
      onStartTimer(task, 0, null, activity);
      toast.success(`Started timer for ${activity.description}`);
    }
  };

  if (!isOpen) return null;

  // Show loading state while fetching data
  if (externalLoading || !task) {
    return createPortal(
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-6xl mx-auto p-12">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">Loading production lot details...</p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Get confirmation groups - handle both dbConfirmationGroups and confirmationGroups
  const confirmationGroups = task.dbConfirmationGroups || task.confirmationGroups || [];
  const dbTasks = task?.dbProductionTasks || [];
  const hasPendingTask = dbTasks.length === 0 || dbTasks.some(t =>
    !t.processingStatusCode || t.processingStatusCode === '1'
  );

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4 animate-in fade-in duration-200 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-6xl mx-auto my-auto transform transition-all animate-in zoom-in-95 duration-200 max-h-[95vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">
                  {currentMode === 'edit' ? 'Edit Production Task' : 'Production Task Details'}
                </h2>
                <p className="text-blue-100 text-xs sm:text-sm">
                  ID: {task?.productionLotId || task?.id || task?.productionTaskId}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Conditional button - Back arrow in edit mode, X in view mode */}
              <button
                onClick={() => {
                  if (currentMode === 'edit') {
                    // In edit mode: cancel edit and return to view mode
                    handleCancelEdit();
                  } else {
                    // In view mode: close the modal
                    onClose();
                  }
                }}
                className="p-1.5 sm:p-2 hover:bg-white/20 rounded-lg transition-colors flex items-center justify-center"
                disabled={loading}
                title={currentMode === 'edit' ? "Go back" : "Close"}
              >
                {currentMode === 'edit' ? (
                  <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                ) : (
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {success ? (
            /* SUCCESS STATE */
            <div className="text-center py-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">Changes Saved Successfully!</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
                Task assignment has been updated.
              </p>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">

              {/* ============ PRODUCTION LOT BASIC INFORMATION ============ */}
              <div className="bg-white dark:bg-gray-800 border-2 border-blue-200 dark:border-blue-800 rounded-xl overflow-hidden">

                {/* Header with Status */}
                <div className="bg-gradient-to-r from-blue-50 dark:from-blue-900/20 to-indigo-50 dark:to-indigo-900/20 px-4 py-3 border-b border-blue-200 dark:border-blue-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">Production Lot Details</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColorClass(task.lifeCycleStatusCode)
                      }`}>
                      {getStatusIcon(task.lifeCycleStatusCode)}
                      {getStatusDisplay(task)}
                    </span>
                  </div>
                </div>

                {/* Basic Information Grid */}
                <div className="p-4">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-3">
                    <Hash className="w-4 h-4" />
                    Basic Information
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {/* Production Lot ID */}
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Clipboard className="w-3 h-3" /> Production Lot ID
                      </span>
                      <p className="font-mono font-semibold text-blue-600 dark:text-blue-400 text-sm sm:text-base mt-1">
                        {task.productionLotId || task.id || '-'}
                      </p>
                    </div>

                    {/* Production Lot UUID */}
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Database className="w-3 h-3" /> Lot UUID
                      </span>
                      <p className="font-mono text-xs text-gray-600 dark:text-gray-400 mt-1 truncate" title={task.productionLotUUID}>
                        {task.productionLotUUID || '-'}
                      </p>
                    </div>

                    {/* Production Order ID */}
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Order ID
                      </span>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base mt-1">
                        {task.productionOrderId || '-'}
                      </p>
                    </div>

                    {/* Production Order UUID */}
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Database className="w-3 h-3" /> Order UUID
                      </span>
                      <p className="font-mono text-xs text-gray-600 dark:text-gray-400 mt-1 truncate" title={task.productionOrderUUID}>
                        {task.productionOrderUUID || '-'}
                      </p>
                    </div>

                    {/* Sales Order No */}
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800">
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium">
                        <Hash className="w-3 h-3" /> Sales Order No
                      </span>
                      <p className="font-mono font-semibold text-emerald-700 dark:text-emerald-400 text-sm sm:text-base mt-1">
                        {task.salesOrderNo || '-'}
                      </p>
                    </div>

                    {/* Product ID */}
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Package className="w-3 h-3" /> Product ID
                      </span>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base mt-1">
                        {task.mainOutputProductId || '-'}
                      </p>
                    </div>

                    {/* Site ID */}
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Factory className="w-3 h-3" /> Site ID
                      </span>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base mt-1">
                        {task.siteId || '-'}
                      </p>
                    </div>

                    {/* Completed Quantity with Status Icons - Icon replaces dash */}
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          Completed
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Quantity value or icon if no quantity */}
                        {task.totalCompletedQuantity ? (
                          <p className={`font-semibold text-lg ${task?.dbProductionTasks?.[0]?.processingStatusCode === '3'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-amber-600 dark:text-amber-400'
                            }`}>
                            {task.totalCompletedQuantity} {task.totalCompletedQuantityUnit || ''}
                          </p>
                        ) : (
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${task?.dbProductionTasks?.[0]?.processingStatusCode === '3'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-2 border-green-200 dark:border-green-800'
                            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-2 border-amber-200 dark:border-amber-800'
                            }`}>
                            {task?.dbProductionTasks?.[0]?.processingStatusCode === '3' ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <Hourglass className="w-5 h-5" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status Codes */}
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Layers className="w-3 h-3" /> Status Codes
                      </span>
                      <div className="mt-1 space-y-1">
                        {task.lifeCycleStatusCode && (
                          <span className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full inline-block mr-1">
                            LC: {task.lifeCycleStatusCode}
                          </span>
                        )}
                        {task.activityProcessStatusCode && (
                          <span className="text-xs bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded-full inline-block mr-1">
                            AP: {task.activityProcessStatusCode}
                          </span>
                        )}
                        {task.reportingPointProcessingStatusCode && (
                          <span className="text-xs bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full inline-block">
                            RP: {task.reportingPointProcessingStatusCode}
                          </span>
                        )}
                      </div>
                    </div>

                  </div>
                </div>

                {/* Production Schedule */}
                {(task.productionStartDate || task.productionEndDate) && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-600">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleSection('schedule')}
                    >
                      <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <CalendarClock className="w-4 h-4" />
                        Production Schedule
                      </h4>
                      <button type="button" className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        {expandedSections.schedule ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {expandedSections.schedule ? 'Hide' : 'Show'}
                      </button>
                    </div>

                    {expandedSections.schedule && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-4 h-4 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <span className="text-xs text-green-700 dark:text-green-400 uppercase font-semibold">Start Date</span>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                              {formatDate(task.productionStartDate)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-4 h-4 text-red-600 dark:text-red-400" />
                          </div>
                          <div>
                            <span className="text-xs text-red-700 dark:text-red-400 uppercase font-semibold">End Date</span>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                              {formatDate(task.productionEndDate) || 'Not set'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Barcode Section */}
                {task.barcodeContentValue && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-600">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleSection('barcode')}
                    >
                      <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <QrCode className="w-4 h-4" />
                        Barcode Information
                      </h4>
                      <button type="button" className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                        {expandedSections.barcode ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {expandedSections.barcode ? 'Hide' : 'Show'}
                      </button>
                    </div>

                    {expandedSections.barcode && (
                      <div className="mt-3 flex flex-col sm:flex-row items-start gap-6">
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm">
                          <img
                            src={`data:image/png;base64,${task.barcodeContentValue}`}
                            alt={`Barcode for Production Lot ${task.productionLotId}`}
                            className="max-h-20 w-auto"
                            onError={(e) => {
                              console.error('Failed to load barcode image');
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML += `
                <div class="text-center p-3">
                  <div class="text-sm text-gray-500 dark:text-gray-400 mb-2">Unable to display barcode</div>
                  <div class="font-mono text-sm bg-gray-100 dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                    ${task.productionLotId}
                  </div>
                </div>
              `;
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Production Lot ID</p>
                            <p className="font-mono text-lg font-bold text-gray-900 dark:text-white">
                              {task.productionLotId}
                            </p>
                          </div>
                          {task.barcodeBase64 && task.barcodeBase64 !== task.barcodeContentValue && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-2">Additional Barcode Data</p>
                              <p className="font-mono text-xs text-gray-600 dark:text-gray-400 break-all max-w-md">
                                {task.barcodeBase64.substring(0, 50)}...
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* USER/TEAM ASSIGNMENT SECTION - Using users/teams arrays from API */}
              <div className="bg-white dark:bg-gray-800 border-2 border-purple-200 dark:border-purple-800 rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-purple-50 dark:from-purple-900/20 to-indigo-50 dark:to-indigo-900/20 px-4 py-3 border-b border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">Task Assignment</h3>
                    {currentMode === 'edit' && (
                      <span className="ml-2 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium">
                        Editable
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-4">
                  {currentMode === 'edit' ? (
                    /* EDIT MODE - Multi-select dropdowns using users/teams arrays */
                    <div className="space-y-6">
                      {/* User Multi-Selection */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Assign to Users (Optional)
                            {fetchingUsers && (
                              <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Loading users...
                              </span>
                            )}
                          </div>
                        </label>

                        {/* Selected Users Tags - Show names from server-resolved map or users array */}
                        {assignmentData.userIds && assignmentData.userIds.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {assignmentData.userIds.map(userId => {
                              const name = getUserName(userId);
                              return name ? (
                                <span
                                  key={userId}
                                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-medium border border-blue-200 dark:border-blue-800"
                                >
                                  <User className="w-3 h-3" />
                                  {name}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAssignmentData(prev => ({
                                        ...prev,
                                        userIds: prev.userIds.filter(id => id !== userId)
                                      }));
                                    }}
                                    className="ml-1 hover:text-blue-900 dark:hover:text-blue-300"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}

                        {/* User Multi-Select Dropdown */}
                        <select
                          value=""
                          onChange={(e) => {
                            const selectedUserId = e.target.value;
                            if (selectedUserId && !assignmentData.userIds?.includes(selectedUserId)) {
                              setAssignmentData(prev => ({
                                ...prev,
                                userIds: [...(prev.userIds || []), selectedUserId]
                              }));
                            }
                          }}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all text-sm dark:bg-gray-700 dark:text-white"
                          disabled={loading || fetchingUsers}
                        >
                          <option value="">-- Add a user --</option>
                          {!fetchingUsers && users.length === 0 ? (
                            <option value="" disabled>No users available</option>
                          ) : (
                            users
                              .filter(user => !assignmentData.userIds?.includes(user.id))
                              .map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.name}
                                </option>
                              ))
                          )}
                        </select>
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          Selected {assignmentData.userIds?.length || 0} user(s)
                        </p>
                      </div>

                      {/* Team Multi-Selection */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Assign to Teams (Optional)
                            {fetchingTeams && (
                              <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Loading teams...
                              </span>
                            )}
                          </div>
                        </label>

                        {/* Selected Teams Tags - Show names from server-resolved map or teams array */}
                        {assignmentData.teamIds && assignmentData.teamIds.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {assignmentData.teamIds.map(teamId => {
                              const name = getTeamName(teamId);
                              return name ? (
                                <span
                                  key={teamId}
                                  className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium border border-purple-200 dark:border-purple-800"
                                >
                                  <Users className="w-3 h-3" />
                                  {name}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAssignmentData(prev => ({
                                        ...prev,
                                        teamIds: prev.teamIds.filter(id => id !== teamId)
                                      }));
                                    }}
                                    className="ml-1 hover:text-purple-900 dark:hover:text-purple-300"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}

                        {/* Team Multi-Select Dropdown */}
                        <select
                          value=""
                          onChange={(e) => {
                            const selectedTeamId = e.target.value;
                            if (selectedTeamId && !assignmentData.teamIds?.includes(selectedTeamId)) {
                              setAssignmentData(prev => ({
                                ...prev,
                                teamIds: [...(prev.teamIds || []), selectedTeamId]
                              }));
                            }
                          }}
                          className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all text-sm dark:bg-gray-700 dark:text-white"
                          disabled={loading || fetchingTeams}
                        >
                          <option value="">-- Add a team --</option>
                          {!fetchingTeams && teams.length === 0 ? (
                            <option value="" disabled>No teams available</option>
                          ) : (
                            teams
                              .filter(team => !assignmentData.teamIds?.includes(team.id))
                              .map(team => (
                                <option key={team.id} value={team.id}>
                                  {team.name}
                                </option>
                              ))
                          )}
                        </select>
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          Selected {assignmentData.teamIds?.length || 0} team(s)
                        </p>
                      </div>

                      {/* Assignment Summary */}
                      {(assignmentData.userIds?.length > 0 || assignmentData.teamIds?.length > 0) && (
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                          <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-400 mb-2">Assignment Summary</h4>
                          <div className="space-y-2 text-sm">
                            {assignmentData.userIds?.length > 0 && (
                              <div>
                                <span className="text-purple-700 dark:text-purple-400">Users ({assignmentData.userIds.length}):</span>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {assignmentData.userIds.map(userId => {
                                    const user = users.find(u => u.id === userId);
                                    return user ? (
                                      <span key={userId} className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded border border-purple-200 dark:border-purple-800">
                                        {user.name}
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            )}
                            {assignmentData.teamIds?.length > 0 && (
                              <div>
                                <span className="text-purple-700 dark:text-purple-400">Teams ({assignmentData.teamIds.length}):</span>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {assignmentData.teamIds.map(teamId => {
                                    const team = teams.find(t => t.id === teamId);
                                    return team ? (
                                      <span key={teamId} className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded border border-purple-200 dark:border-purple-800">
                                        {team.name}
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* VIEW MODE - Display assignments using users/teams arrays */
                    <div className="space-y-4">
                      {/* Users List */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-2 mb-3">
                          <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Assigned Users</span>
                          {assignmentData.userIds?.length > 0 && (
                            <span className="ml-auto text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full">
                              {assignmentData.userIds.length}
                            </span>
                          )}
                        </div>

                        {assignmentData.userIds && assignmentData.userIds.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {assignmentData.userIds.map((userId) => {
                              const name = getUserName(userId);
                              return name ? (
                                <span
                                  key={userId}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-50 dark:from-blue-900/20 to-indigo-50 dark:to-indigo-900/20 text-blue-700 dark:text-blue-400 rounded-full text-xs font-medium border border-blue-200 dark:border-blue-800 shadow-sm"
                                >
                                  <User className="w-3 h-3" />
                                  <span>{name}</span>
                                </span>
                              ) : (
                                <span
                                  key={userId}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-600"
                                >
                                  <User className="w-3 h-3" />
                                  <span>Unknown User</span>
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-gray-500 dark:text-gray-400 text-sm italic">No users assigned</p>
                        )}
                      </div>

                      {/* Teams List */}
                      <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-2 mb-3">
                          <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Assigned Teams</span>
                          {assignmentData.teamIds?.length > 0 && (
                            <span className="ml-auto text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-0.5 rounded-full">
                              {assignmentData.teamIds.length}
                            </span>
                          )}
                        </div>

                        {assignmentData.teamIds && assignmentData.teamIds.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {assignmentData.teamIds.map((teamId) => {
                              const name = getTeamName(teamId);
                              return name ? (
                                <span
                                  key={teamId}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-purple-50 dark:from-purple-900/20 to-pink-50 dark:to-pink-900/20 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium border border-purple-200 dark:border-purple-800 shadow-sm"
                                >
                                  <Users className="w-3 h-3" />
                                  <span>{name}</span>
                                </span>
                              ) : (
                                <span
                                  key={teamId}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-600"
                                >
                                  <Users className="w-3 h-3" />
                                  <span>Unknown Team</span>
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-gray-500 dark:text-gray-400 text-sm italic">No teams assigned</p>
                        )}
                      </div>

                      {/* If no assignments at all */}
                      {(!assignmentData.userIds || assignmentData.userIds.length === 0) &&
                        (!assignmentData.teamIds || assignmentData.teamIds.length === 0) && (
                          <div className="text-center py-6 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                            <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No users or teams assigned</p>
                            <p className="text-xs text-gray-400 mt-1">This task is currently unassigned</p>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>

              {/* CONFIRMATION GROUPS SECTION */}
              {confirmationGroups.length > 0 && (
                <div className="bg-white dark:bg-gray-800 border-2 border-purple-200 dark:border-purple-800 rounded-xl overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-purple-50 dark:from-purple-900/20 to-indigo-50 dark:to-indigo-900/20 px-4 py-3 border-b border-purple-200 dark:border-purple-800 flex items-center justify-between cursor-pointer"
                    onClick={() => toggleSection('confirmationGroups')}
                  >
                    <div className="flex items-center gap-2">
                      <Layers className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      <h3 className="font-semibold text-gray-900 dark:text-white">Confirmation Groups</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-medium">
                        {confirmationGroups.length} {confirmationGroups.length === 1 ? 'Group' : 'Groups'}
                      </span>
                      <button type="button" className="text-xs text-purple-600 dark:text-purple-400">
                        {expandedSections.confirmationGroups ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {expandedSections.confirmationGroups && confirmationGroups.map((group, groupIndex) => (
                    <div key={group.confirmationGroupUUID || groupIndex} className="p-4 border-t border-gray-100 dark:border-gray-700 first:border-t-0">
                      {/* Group Header */}
                      <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800 mb-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <div>
                            <span className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold">Group UUID</span>
                            <p className="font-mono text-xs truncate text-gray-700 dark:text-gray-300" title={group.confirmationGroupUUID}>
                              {group.confirmationGroupUUID || '-'}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold">Processor Type</span>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{group.processorObjectTypeCode || '-'}</p>
                          </div>
                          {group.lifeCycleStatusCode && (
                            <div>
                              <span className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold">Status</span>
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{group.lifeCycleStatusCode}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Activities - Using dbActivities */}
                      {group.dbActivities && group.dbActivities.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                            <Activity className="w-4 h-4 text-purple-500" />
                            Activities ({group.dbActivities.length})
                          </h5>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Activity ID</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Description</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Operation</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Duration</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.dbActivities.map((activity, idx) => (
                                  <tr key={activity.activityUUID || idx} className="hover:bg-blue-50 dark:hover:bg-blue-900/20">
                                    <td className="px-2 py-1.5 font-mono text-blue-600 dark:text-blue-400">{activity.activityId || '-'}</td>
                                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{activity.activityDescription || '-'}</td>
                                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{activity.operationId || '-'}</td>
                                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{formatDuration(activity.plannedDuration)}</td>
                                    <td className="px-2 py-1.5">
                                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${activity.confirmationCompletedIndicator
                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                        : activity.lifeCycleStatusCode === '3'
                                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                                        }`}>
                                        {activity.confirmationCompletedIndicator ? 'Completed' :
                                          activity.lifeCycleStatusCode === '3' ? 'In Progress' : 'Pending'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Material Inputs - Using dbMaterialInputs */}
                      {group.dbMaterialInputs && group.dbMaterialInputs.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                            <Boxes className="w-4 h-4 text-blue-500" />
                            Material Inputs ({group.dbMaterialInputs.length})
                          </h5>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Product ID</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Description</th>
                                  <th className="text-right px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Planned</th>
                                  <th className="text-right px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Confirmed</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Source</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.dbMaterialInputs.map((input, idx) => (
                                  <tr key={input.materialUUID || idx} className="hover:bg-blue-50 dark:hover:bg-blue-900/20">
                                    <td className="px-2 py-1.5 font-mono text-blue-600 dark:text-blue-400">{input.productId || '-'}</td>
                                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{input.productDescription || '-'}</td>
                                    <td className="px-2 py-1.5 text-right font-medium text-gray-700 dark:text-gray-300">
                                      {input.plannedQuantity ?? '-'} {input.plannedQuantityUnit || ''}
                                    </td>
                                    <td className="px-2 py-1.5 text-right">
                                      <span className={input.confirmationFinished ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
                                        {input.confirmationFinished ? '✓' : '-'}
                                      </span>
                                    </td>
                                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{input.sourceLogisticsAreaId || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Material Outputs - Using dbMaterialOutputs */}
                      {group.dbMaterialOutputs && group.dbMaterialOutputs.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                            <Package className="w-4 h-4 text-green-500" />
                            Material Outputs ({group.dbMaterialOutputs.length})
                          </h5>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Product ID</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Description</th>
                                  <th className="text-right px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Planned</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Unit</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Activity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.dbMaterialOutputs.map((output, idx) => (
                                  <tr key={output.materialUUID || idx} className="hover:bg-blue-50 dark:hover:bg-blue-900/20">
                                    <td className="px-2 py-1.5 font-mono text-blue-600 dark:text-blue-400">{output.productId || '-'}</td>
                                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{output.productDescription || '-'}</td>
                                    <td className="px-2 py-1.5 text-right font-medium text-gray-700 dark:text-gray-300">{output.plannedQuantity ?? '-'}</td>
                                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{output.plannedQuantityUnit || '-'}</td>
                                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">{output.activityId || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {group.dbReportingPoints && group.dbReportingPoints.length > 0 && (
                        <div className="mt-4">
                          <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-amber-500" />
                            Reporting Points ({group.dbReportingPoints.length})
                          </h5>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Reporting Point ID</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">UUID</th>
                                  <th className="text-right px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Planned Qty</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Unit</th>
                                  <th className="text-right px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Confirmed Qty</th>
                                  <th className="text-right px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Open Qty</th>
                                  <th className="text-left px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Status</th>
                                  <th className="text-center px-2 py-1.5 text-gray-600 dark:text-gray-400 font-semibold">Completed</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.dbReportingPoints.map((point, idx) => (
                                  <tr key={point.reportingPointUUID || idx} className="hover:bg-amber-50 dark:hover:bg-amber-900/20">
                                    <td className="px-2 py-1.5 font-mono text-amber-600 dark:text-amber-400 font-medium">
                                      {point.reportingPointID || '-'}
                                    </td>
                                    <td className="px-2 py-1.5 font-mono text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px]" title={point.reportingPointUUID}>
                                      {point.reportingPointUUID ? point.reportingPointUUID.substring(0, 8) + '...' : '-'}
                                    </td>
                                    <td className="px-2 py-1.5 text-right font-medium text-gray-700 dark:text-gray-300">
                                      {point.plannedQuantity ?? '-'}
                                    </td>
                                    <td className="px-2 py-1.5 text-gray-700 dark:text-gray-300">
                                      {point.plannedQuantityUnit || '-'}
                                    </td>
                                    <td className="px-2 py-1.5 text-right font-medium text-green-600 dark:text-green-400">
                                      {point.totalConfirmedQuantity ?? '0'}
                                    </td>
                                    <td className="px-2 py-1.5 text-right font-medium text-blue-600 dark:text-blue-400">
                                      {point.openQuantity ?? '-'}
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${point.processingStatusCode === 'Completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                                        point.processingStatusCode === 'InProgress' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                                          'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                                        }`}>
                                        {point.processingStatusCode || '-'}
                                      </span>
                                    </td>
                                    <td className="px-2 py-1.5 text-center">
                                      {point.confirmationFinished ? (
                                        <CheckCircle className="w-3.5 h-3.5 text-green-500 mx-auto" />
                                      ) : (
                                        <span className="text-gray-300 dark:text-gray-600">—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* PRODUCTION TASKS SECTION - Using dbProductionTasks */}
              {task.dbProductionTasks && task.dbProductionTasks.length > 0 && (
                <div className="bg-white dark:bg-gray-800 border-2 border-amber-200 dark:border-amber-800 rounded-xl overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-amber-50 dark:from-amber-900/20 to-orange-50 dark:to-orange-900/20 px-4 py-3 border-b border-amber-200 dark:border-amber-800 flex items-center justify-between cursor-pointer"
                    onClick={() => toggleSection('productionTasks')}
                  >
                    <div className="flex items-center gap-2">
                      <Settings className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      <h3 className="font-semibold text-gray-900 dark:text-white">Production Tasks</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium">
                        {task.dbProductionTasks.length}
                      </span>
                      <button type="button" className="text-xs text-amber-600 dark:text-amber-400">
                        {expandedSections.productionTasks ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {expandedSections.productionTasks && (
                    <div className="p-4">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 font-semibold">Task ID</th>
                              <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 font-semibold">Operation</th>
                              <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 font-semibold">Status</th>
                              <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 font-semibold">Barcode</th>
                              <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 font-semibold">Created</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {task.dbProductionTasks.map((subTask, index) => (
                              <tr key={subTask.productionTaskUUID || index} className="hover:bg-amber-50/50 dark:hover:bg-amber-900/20">
                                <td className="px-3 py-2 font-mono font-semibold text-amber-600 dark:text-amber-400">
                                  {subTask.productionTaskId}
                                </td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                  {subTask.operationTypeCode || '-'}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${subTask.processingStatusCode === '3' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                                    subTask.processingStatusCode === '2' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                                      'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                    }`}>
                                    {subTask.processingStatusCode === '3' ? 'Completed' :
                                      subTask.processingStatusCode === '2' ? 'In Progress' : 'Pending'}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  {subTask.barcodeBase64 ? (
                                    <img
                                      src={`data:image/png;base64,${subTask.barcodeBase64}`}
                                      alt={`Task ${subTask.productionTaskId} barcode`}
                                      className="h-6 w-auto"
                                    />
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                  {subTask.createdAt ? formatDate(subTask.createdAt) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-400 text-sm">Error</p>
                      <p className="text-xs sm:text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 sm:px-5 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
            disabled={loading}
          >
            Close
          </button>

          {currentMode === 'edit' ? (
            <button
              onClick={handleSave}
              disabled={loading || fetchingUsers || fetchingTeams}
              className="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          ) : onStartTimer ? (() => {
            // Check if there's a running timer for this task
            const taskId = task?.productionLotId || '';
            const orgTaskId = task?.dbProductionTasks[0]?.productionTaskId || '';

            const isTimerRunning = runningEntries.some(e =>
              e.referenceProductionTaskId === taskId ||
              e.productionTaskId === orgTaskId ||
              String(e.referenceProductionTaskId) === String(taskId) ||
              String(e.productionTaskId) === String(orgTaskId)
            );

            // Status-aware: if any task is In Process (code '2' or '3'), show "Continue Working"
            const isInProgress = dbTasks.some(t =>
              t.processingStatusCode === '2' || t.processingStatusCode === '3'
            );
            const buttonLabel = isInProgress ? 'Continue Working' : 'Start Timer';

            // If task is completed, show disabled button with explanation
            if (isTaskCompleted) {
              return (
                <div className="flex items-center gap-3">
                  <div className="relative group">
                    <div className="px-4 sm:px-6 py-2.5 bg-gray-400 dark:bg-gray-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 text-sm cursor-not-allowed opacity-50">
                      <Timer className="w-4 h-4" />
                      Completed - Cannot Start
                    </div>
                    {/* Tooltip on hover */}
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                      This task is completed so this tasks cannot be restarted.
                    </div>
                  </div>
                </div>
              );
            }

            return isTimerRunning ? (
              <div className="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-teal-500/20 to-emerald-500/20 border-2 border-teal-400 dark:border-teal-500 text-teal-700 dark:text-teal-300 rounded-lg font-medium flex items-center justify-center gap-2 text-sm animate-pulse">
                <Timer className="w-4 h-4" />
                Timer Running
              </div>
            ) : (
              <>
                <button
                  onClick={handleStartTimerClick}
                  disabled={startingTask || fetchingHistory}
                  className="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-emerald-600 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {fetchingHistory ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Timer className="w-4 h-4" />
                      {buttonLabel}
                    </>
                  )}
                </button>

                {/* ACTIVITY SELECTION MODAL - ALWAYS SHOWN WHEN START TIMER CLICKED */}
                {showPreviousSessions && (() => {
                  return (
                    <div
                      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPreviousSessions(false);
                      }}
                    >
                      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                      <div
                        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Header */}
                        <div className="flex-shrink-0 p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="p-3 bg-amber-100 dark:bg-amber-900/40 rounded-xl">
                                <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                  Select Activity
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  Choose an activity to start or continue working
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Activity Tabs */}
                        <div className="flex-shrink-0 flex border-b overflow-x-auto px-4 bg-gray-50 dark:bg-gray-800/50">
                          <button
                            onClick={() => setSelectedActivityTab(null)}
                            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${!selectedActivityTab
                                ? 'border-b-2 border-teal-500 text-teal-600 bg-white dark:bg-gray-800'
                                : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                              }`}
                          >
                            <div className="flex items-center gap-2">
                              <Layers className="w-4 h-4" />
                              All Activities ({allActivities.length})
                            </div>
                          </button>

                          {allActivities.map((activity) => {
                            const history = activityHistoryMap.get(activity.activityId) || [];
                            return (
                              <button
                                key={activity.activityId}
                                onClick={() => setSelectedActivityTab(activity)}
                                className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors ${selectedActivityTab?.activityId === activity.activityId
                                    ? 'border-b-2 border-teal-500 text-teal-600 bg-white dark:bg-gray-800'
                                    : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                                  }`}
                              >
                                <Activity className="w-4 h-4" />
                                {activity.description}
                                {history.length > 0 && (
                                  <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs">
                                    {history.length}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-h-0 overflow-y-auto p-4">
                          {selectedActivityTab === null ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {allActivities.map((activity) => {
                                const history = activityHistoryMap.get(activity.activityId) || [];
                                const latestVersion = getLatestVersion(history);
                                const totalTime = history.reduce(
                                  (sum, h) =>
                                    sum + Math.round((h.hours || 0) * 3600) + (h.accumulatedSeconds || 0),
                                  0
                                );

                                return (
                                  <div
                                    key={activity.activityId}
                                    className="border-2 rounded-lg p-4 hover:shadow-md transition-all border-gray-200 dark:border-gray-700"
                                  >
                                    <div className="flex items-start justify-between mb-3">
                                      <div>
                                        <h4 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                          <Activity className="w-4 h-4 text-purple-500" />
                                          {activity.description}
                                        </h4>
                                        <p className="text-xs text-gray-500 mt-1">
                                          {activity.plannedDuration || 'Duration not specified'}
                                        </p>
                                      </div>
                                    </div>

                                    {history.length > 0 ? (
                                      <div className="space-y-2 mb-3">
                                        <div className="flex items-center justify-between text-xs">
                                        </div>
                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-gray-500">Sessions</span>
                                          <span className="font-semibold text-gray-700">
                                            {history.length}
                                          </span>
                                        </div>
                                        {latestVersion && (
                                          <div className="flex items-center justify-between text-xs">
                                            <span className="text-gray-500">Latest Version</span>
                                            <span className="font-semibold text-blue-600">
                                              v{latestVersion.version} ({latestVersion.employeeName})
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-400 italic mb-3">
                                        No previous sessions - This will be a new session
                                      </p>
                                    )}

                                    <button
                                      onClick={() => handleContinueFromActivity(activity, latestVersion)}
                                      className="w-full px-3 py-1.5 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 transition-colors flex items-center justify-center gap-1"
                                    >
                                      <Play className="w-3 h-3" />
                                      {history.length > 0 && latestVersion
                                        ? `Continue ${activity.description} (v${latestVersion.version})`
                                        : `Start ${activity.description}`}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                  {selectedActivityTab.description} - Version History
                                </h3>
                              </div>

                              {activityHistoryMap
                                .get(selectedActivityTab.activityId)
                                ?.sort((a, b) => (b.version || 0) - (a.version || 0))
                                .map((entry) => {
                                  const totalSecs =
                                    Math.round((entry.hours || 0) * 3600) +
                                    (entry.accumulatedSeconds || 0);

                                  return (
                                    <div
                                      key={entry.id}
                                      className={`p-4 rounded-xl border transition-all ${entry.isLatestVersion
                                        ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/30'
                                        : 'border-gray-200 dark:border-gray-700'
                                        }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          <span
                                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${entry.isLatestVersion
                                              ? 'bg-teal-100 text-teal-700'
                                              : 'bg-gray-100 text-gray-600'
                                              }`}
                                          >
                                            v{entry.version}
                                          </span>
                                          <div>
                                            <p className="font-semibold text-gray-900 dark:text-white">
                                              {entry.employeeName}
                                            </p>
                                            {/* FIXED: Use formatDateTime instead of new Date(entry.date).toLocaleString() */}
                                            <p className="text-xs text-gray-500">
                                              {formatDateTime(entry)}
                                            </p>
                                            {/* Also show the time range if available */}
                                            {entry.startTime && (
                                              <p className="text-xs text-gray-400 font-mono mt-0.5">
                                                {(() => {
                                                  const [hours, minutes] = entry.startTime.split(':');
                                                  const hour = parseInt(hours, 10);
                                                  const ampm = hour >= 12 ? 'PM' : 'AM';
                                                  const hour12 = hour % 12 || 12;
                                                  const formattedStart = `${hour12}:${minutes} ${ampm}`;

                                                  if (entry.endTime) {
                                                    const [endHours, endMinutes] = entry.endTime.split(':');
                                                    const endHour = parseInt(endHours, 10);
                                                    const endAmpm = endHour >= 12 ? 'PM' : 'AM';
                                                    const endHour12 = endHour % 12 || 12;
                                                    const formattedEnd = `${endHour12}:${endMinutes} ${endAmpm}`;
                                                    return `${formattedStart} → ${formattedEnd}`;
                                                  }
                                                  return `${formattedStart} → Running`;
                                                })()}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <p className="font-mono font-bold text-indigo-600">
                                            {formatTotalTime(totalSecs)}
                                          </p>
                                          {entry.isLatestVersion && (
                                            <p className="text-xs text-teal-600 font-semibold">
                                              Latest Version
                                            </p>
                                          )}
                                        </div>
                                      </div>

                                      {entry.description && (
                                        <p className="mt-2 text-sm text-gray-600">
                                          {entry.description}
                                        </p>
                                      )}

                                      {entry.isLatestVersion && (
                                        <button
                                          onClick={() => handleContinueFromActivity(selectedActivityTab, entry)}
                                          className="mt-3 px-3 py-1.5 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 transition-colors"
                                        >
                                          Continue from v{entry.version}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}

                              {(!activityHistoryMap.get(selectedActivityTab.activityId) ||
                                activityHistoryMap.get(selectedActivityTab.activityId).length === 0) && (
                                  <div className="text-center py-8">
                                    <p className="text-gray-500 dark:text-gray-400">
                                      No previous sessions found for this activity.
                                    </p>
                                    <button
                                      onClick={() => handleContinueFromActivity(selectedActivityTab, null)}
                                      className="mt-4 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                                    >
                                      Start New Session
                                    </button>
                                  </div>
                                )}
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="flex-shrink-0 p-4 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-800">
                          <div>
                            {selectedActivityTab !== null && (
                              <button
                                onClick={() => setSelectedActivityTab(null)}
                                className="text-blue-600 hover:underline text-sm"
                              >
                                ← Back to all activities
                              </button>
                            )}
                          </div>

                          <button
                            onClick={() => setShowPreviousSessions(false)}
                            className="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Pending Task Confirmation Dialog */}
                {showStartTaskConfirm && (
                  <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setShowStartTaskConfirm(false); }}>
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                    <div
                      className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                          <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white text-lg">Task is Pending</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Production Task #{dbTasks[0]?.productionTaskId || taskId}</p>
                        </div>
                      </div>

                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-6">
                        This production task is currently <span className="font-semibold text-amber-600 dark:text-amber-400">Pending</span>. Starting the timer will move it to <span className="font-semibold text-teal-600 dark:text-teal-400">In Process</span>.
                      </p>

                      {continueFromEntry && (
                        <div className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-xs text-teal-700 dark:text-teal-400 text-center mb-4">
                          Continuing from <strong>v{continueFromEntry.version}</strong> by <strong>{continueFromEntry.employeeName}</strong>
                        </div>
                      )}

                      <p className="text-gray-500 dark:text-gray-400 text-xs mb-6">
                        Do you want to start the timer and mark this task as In Process?
                      </p>

                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => { setShowStartTaskConfirm(false); setContinueFromEntry(null); }}
                          disabled={startingTask}
                          className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            setStartingTask(true);
                            try {
                              // Find the first pending task SAP ID to mark as In Process
                              const pendingTask = dbTasks.find(t => !t.processingStatusCode || t.processingStatusCode === '1');
                              if (pendingTask?.productionTaskId) {
                                await startProductionTask(pendingTask.productionTaskId);
                              }
                              setShowStartTaskConfirm(false);
                              setPendingTaskHandled(true);
                              // Pass accumulated seconds / parent from continueFromEntry
                              const accSecs = continueFromEntry
                                ? Math.round((continueFromEntry.hours || 0) * 3600) + (continueFromEntry.accumulatedSeconds || 0)
                                : 0;
                              const parentId = continueFromEntry?.id || null;
                              onStartTimer(task, accSecs, parentId, selectedActivity);
                              setContinueFromEntry(null);
                            } catch (err) {
                              console.error('Failed to start production task:', err);
                              toast.error(err.message || 'Failed to update task status');
                            } finally {
                              setStartingTask(false);
                            }
                          }}
                          disabled={startingTask}
                          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-medium hover:from-teal-600 hover:to-emerald-600 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                        >
                          {startingTask ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Starting...
                            </>
                          ) : (
                            <>
                              <Timer className="w-4 h-4" />
                              Start Timer & Set In Process
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })() : (
            <button
              onClick={() => setCurrentMode('edit')}
              className="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Edit2 className="w-4 h-4" />
              Edit Task
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductionTaskDetailModal;